import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Congregation } from '../lib/database.types';

interface CongregationContextType {
  congregations: Congregation[];
  active: Congregation | null;
  setActive: (congregation: Congregation) => void;
  loading: boolean;
  error: string | null;
}

const CongregationContext = createContext<CongregationContextType | undefined>(undefined);

const STORAGE_KEY = 'kerkplanner_congregation';

export function CongregationProvider({ children }: { children: ReactNode }) {
  const [congregations, setCongregations] = useState<Congregation[]>([]);
  const [active, setActiveState] = useState<Congregation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCongregations();
  }, []);

  async function fetchCongregations() {
    const { data, error: fetchError } = await supabase
      .from('congregations')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (fetchError) {
      setError('Gemeenten konden niet worden geladen. Controleer je verbinding en laad de pagina opnieuw.');
      setLoading(false);
      return;
    }

    if (data && data.length > 0) {
      const congregations = data as Congregation[];
      setCongregations(congregations);

      const storedId = localStorage.getItem(STORAGE_KEY);
      const stored = storedId ? congregations.find(c => c.id === storedId) : null;
      const primary = congregations.find(c => c.is_primary) || congregations[0];

      setActiveState(stored || primary);
    }
    setLoading(false);
  }

  const setActive = useCallback((congregation: Congregation) => {
    setActiveState(congregation);
    localStorage.setItem(STORAGE_KEY, congregation.id);
    updateThemeColor(congregation.color);
    updatePrimaryColor(congregation.color);
  }, []);

  useEffect(() => {
    if (active) {
      updateThemeColor(active.color);
      updatePrimaryColor(active.color);
    }
  }, [active]);

  return (
    <CongregationContext.Provider value={{ congregations, active, setActive, loading, error }}>
      {children}
    </CongregationContext.Provider>
  );
}

export function useCongregation() {
  const context = useContext(CongregationContext);
  if (context === undefined) {
    throw new Error('useCongregation must be used within a CongregationProvider');
  }
  return context;
}

function updateThemeColor(color: string) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = color;
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : null;
}

function darkenHex(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.round(rgb.b * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function updatePrimaryColor(color: string) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', color);
  root.style.setProperty('--color-primary-hover', darkenHex(color, 0.15));
  const rgb = hexToRgb(color);
  if (rgb) {
    root.style.setProperty('--color-primary-light', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`);
  }
}
